import {
  LoginSource,
  useCompleteSSORedirectLoginMutation,
} from "@/authentication/redirect/util/__generated__/useCompleteSSORedirectLoginMutation.graphql"
import { sendSentryAnException } from "@/core/sentryHandler"
import Relay from "@/relay/relayUtils"
import ROUTE_NAMES from "@/core/route/util/routeNames"
import useUserTimezone from "@/user/util/useUserTimezone"
import { SocialLoginDestination } from "@components/social-login/SocialLogin"
import { useQueryParams } from "@utils/url/urlUtils"
import { useState } from "react"
import { graphql } from "relay-runtime"
import { decodeBase64 } from "stream-chat"
import { useCompleteSSORedirectVerifyMutation } from "./__generated__/useCompleteSSORedirectVerifyMutation.graphql"

export type CompleteSSOLoginParams = {
  token: string
  stytch_token_type: string
  slug: string
  destination: string
  loginSource: string
  action?: string
}

export type SSOLoginState = {
  state: string
  message?: string
}

export function useCompleteSSORedirect() {
  const { token, destination, stytch_token_type, loginSource, action } =
    useQueryParams<CompleteSSOLoginParams>()

  const [status, setStatus] = useState<SSOLoginState>({ state: "pending" })
  const userTimezone = useUserTimezone()

  const loginMutation = Relay.useAsyncMutation<useCompleteSSORedirectLoginMutation>(
    graphql`
      mutation useCompleteSSORedirectLoginMutation($input: CompleteSSOLoginInput!) {
        completeSSOLogin(input: $input) {
          redirectOverride
          errors {
            field
            message
          }
        }
      }
    `
  )

  const verifyMutation = Relay.useAsyncMutation<useCompleteSSORedirectVerifyMutation>(
    graphql`
      mutation useCompleteSSORedirectVerifyMutation($input: VerifyAuthProviderInput!) {
        verifyAuthProvider(input: $input) {
          data
          errors {
            field
            message
          }
        }
      }
    `
  )

  async function completeSSORedirect(): Promise<void> {
    // Check that the token type is correct
    if (stytch_token_type !== "sso") {
      setStatus({ state: "error", message: "Invalid request [001]." })
      return
    }

    // Extract the destination information
    const redirectDestination: SocialLoginDestination = JSON.parse(
      decodeBase64(decodeURIComponent(destination))
    )

    // We should ALWAYS have a location state that we are redirecting to for SSO since the redirect
    // will be added as the destination when clicking the SSO button
    if (!redirectDestination.locationState?.redirectUrl) {
      setStatus({ state: "error", message: "Invalid request [003]." })
      return
    }

    // The redirect URL from a login request will always point to the initiating
    // domain. If a customer has a custom domain but is authenticating with the disco
    // subdomain we were authenticating them properly to the custom domain but then
    // redirecting them back to the non-custom domain. We had made a different change
    // before this to always redirect to the "primary" domain because before when we
    // were completing the login we'd set an "org specific cookie" on the main domain
    // but attempt to make the request to "do the cookie dance" from the main domain but
    // the user didn't have a standard session token so the request was being rejected.
    const u = new URL(redirectDestination.locationState.redirectUrl)
    const thisDomain = new URL(window.location.href)
    if (u.host !== thisDomain.host) {
      thisDomain.host = u.host
      window.location.href = thisDomain.toString()
      return
    }

    if (action === "verify") {
      await completeSSOVerification()
      return
    }

    await completeSSOLogin(u.toString())
  }

  async function completeSSOLogin(redirectUrl: string): Promise<void> {
    if (!loginSource) {
      setStatus({ state: "error", message: "Invalid request [002]." })
      return
    }

    let dest = redirectUrl

    try {
      const res = await loginMutation({
        input: {
          token,
          timezone: userTimezone,
          loginSource: loginSource as LoginSource,
        },
      })
      if (res.completeSSOLogin.errors?.length) {
        for (const error of res.completeSSOLogin.errors) {
          if (error.field === "ssoLogin") {
            setStatus({ state: "error", message: error.message })
            return
          } else if (error.field === "tokenInvalid") {
            // The user has already used the token and maybe hit back one too many times
            // so we will just redirect them to the app root
            window.location.href = BASE_DOMAIN_URL
            return
          }
        }
        setStatus({
          state: "error",
          message: "An error occurred when logging you in, please try again later [001].",
        })
        return
      }

      // If the backend wants us to override the redirect, let's do that now
      // This is mainly used for allowing just in time provisioning
      dest = res.completeSSOLogin.redirectOverride || dest
    } catch (error) {
      setStatus({
        state: "error",
        message: "An error occurred when logging you in, please try again later [002].",
      })
      // Send the error to sentry
      sendSentryAnException(error, {
        extra: {
          title: "useCompleteSSORedirect",
        },
      })
      return
    }

    // Send them to the right destination
    window.location.href = dest
  }

  async function completeSSOVerification(): Promise<void> {
    try {
      const res = await verifyMutation({ input: { token } })
      if (res.verifyAuthProvider.errors?.length) {
        for (const error of res.verifyAuthProvider.errors) {
          if (error.field === "authProvider") {
            setStatus({ state: "error", message: error.message })
            return
          }
        }
      }

      if (window.opener && !window.opener.closed) {
        window.opener.postMessage({ type: "SSO_VERIFIED" }, "*")
      }

      window.location.href = `${ROUTE_NAMES.SSO_TEST_CONNECTION}?status=success`
    } catch (error) {
      const errorMessage =
        "We couldn't verify your identity. Close this tab and try again,"
      sendSentryAnException(error, {
        extra: {
          title: "completeSSOVerification",
        },
      })
      window.location.href = `${
        ROUTE_NAMES.SSO_TEST_CONNECTION
      }?status=failed&message=${encodeURIComponent(errorMessage)}`
    }
  }

  return { completeSSORedirect, status }
}
